import React, { useEffect, useState } from 'react';
import badge from "../../assets/badge.svg";
import { CheckCircle } from 'lucide-react';
import { useDashboard } from '../Context/Dashboardcontext';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { getProfile, checkOutAiPackageById } from '../../ApiController/ApiService';

function Package() {
  const { packages, fetchPackageData, PaymentForPackage, loading: dataLoading } = useDashboard();

  const navigate = useNavigate();

  const [paymentLoading, setPaymentLoading] = useState(false);

  const [userPackage, setUserPackage] = useState("");
  const [userPackageId, setUserPackageId] = useState(null);
  const [userAiPackageId, setUserAiPackageId] = useState(null);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);

  // Default to "Regular"
  const [activeTab, setActiveTab] = useState("Regular");

  useEffect(() => {
    fetchPackageData();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setUserPackage("");
        setUserPackageId(null);
        setUserAiPackageId(null);
        setHasUsedFreeTrial(false);
        return;
      }

      try {
        const profileData = await getProfile();
        const pkgName = profileData?.packageId?.name?.toLowerCase();
        setUserPackageId(profileData?.packageId?._id || null);
        setUserAiPackageId(profileData?.aiPackageId?._id || null);
        setHasUsedFreeTrial(profileData?.isFreePackage || true);
        setUserPackage(pkgName);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        setUserPackage('starter');
      }
    };
    fetchProfile();
  }, []);

  // Filter Logic
  const filteredPackages = packages.filter(pkg => {
    if (activeTab === "Ai") {
      return pkg.type === "Ai";
    } else {
      return pkg.type === "Regular";
    }
  });

  const handleChoosePlan = async (plan) => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      Swal.fire({
        title: "Login Required",
        text: "Please login to continue with this plan.",
        icon: "warning",
        confirmButtonColor: "#04447A",
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) navigate("/login");
      });
      return;
    }

    if (plan.isFree && hasUsedFreeTrial) {
      Swal.fire({
        title: "Already Used",
        text: "You have already experienced the free trial and cannot select it again.",
        icon: "info",
        confirmButtonColor: "#04447A",
        confirmButtonText: "OK",
      });
      return;
    }

    const currentPackage = packages.find(
      (p) => p.name.toLowerCase() === userPackage
    );
    const currentPrice = currentPackage?.price || 0;
    const selectedPrice = plan.isFree ? 0 : plan.price;

    if (selectedPrice < currentPrice) {
      const result = await Swal.fire({
        title: "Confirm Downgrade",
        text: `You are currently on a higher plan ($${currentPrice}). Are you sure you want to switch to this plan ($${selectedPrice})?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#04447A",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, downgrade",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
    }

    try {
      setPaymentLoading(true);

      Swal.fire({
        title: plan.isFree ? "Activating Plan" : "Processing Payment",
        text: plan.isFree
          ? "Please wait while your plan is being activated..."
          : "Please wait, your payment is in progress...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      let res;
      if (plan.type === 'Ai') {
        const ress = await checkOutAiPackageById(plan._id,{companyType:"Dispax"});

        console.log("AI Package Checkout Response:", ress.data);
        res = ress.data;
      } else {
        res = await PaymentForPackage(plan._id,{companyType:"Dispax"});
      }

      Swal.close();

      if (res?.data?.session?.url || res.data?.url) {
        window.open(res?.data?.session?.url || res.data.url, "_self");
      } else {
        Swal.fire({
          title: "Failed",
          text: "Could not proceed with this plan.",
          icon: "error",
          confirmButtonColor: "#04447A",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.close();
      console.error("Payment Error:", error);
      Swal.fire({
        title: "Failed",
        text: "There was an issue processing your plan.",
        icon: "error",
        confirmButtonColor: "#04447A",
        confirmButtonText: "OK",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div
      className="py-16 sm:py-20 px-4 sm:px-6 text-white overflow-x-hidden bg-black"
    >
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-[32px] sm:text-[40px] md:text-[44px] font-medium mb-6">
            Membership Plan
          </h1>

          {/* Tab Switcher Buttons */}
          <div className="flex justify-center mb-6">
            <div className="bg-[#111] p-1 rounded-xl border border-white/10 inline-flex">
              <button
                onClick={() => setActiveTab("Regular")}
                className={`px-6 py-2 rounded-lg transition-all text-sm sm:text-base font-medium ${activeTab === "Regular"
                    ? "bg-[#0055F9] text-white shadow-lg"
                    : "text-gray-400 hover:text-white"
                  }`}
              >
                Regular Plans
              </button>
              <button
                onClick={() => setActiveTab("Ai")}
                className={`px-6 py-2 rounded-lg transition-all text-sm sm:text-base font-medium ${activeTab === "Ai"
                    ? "bg-[#0055F9] text-white shadow-lg"
                    : "text-gray-400 hover:text-white"
                  }`}
              >
                AI Plans
              </button>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div
          style={{ fontFamily: "Poppins" }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
        >
          {filteredPackages.map((plan) => {
            // Check if we have any stats to show to conditionally render the box
            const hasStats = plan.truck > 0 || plan.trackingCount > 0 || plan.loadsList > 0;

            return (
              <div
                key={plan._id}
                className={`relative flex flex-col p-4 sm:p-6 rounded-[20px] border-2 transition-all ${plan.isFree || plan.price === 0 || plan.price === '0'
                    ? "border-white/10 bg-[#050505] hover:border-purple-500/30"
                    : "border-purple-600/50 bg-[#0a0a0a] shadow-[0_0_20px_rgba(147,51,234,0.1)] hover:shadow-[0_0_30px_rgba(147,51,234,0.25)]"
                  }`}
              >
                {/* Badge for Pro plan */}
                {plan.name.toLowerCase().includes("pro") && (
                  <div className="absolute top-[-40px] sm:top-[-60px] right-[-30px] sm:right-[-54px]">
                    <img
                      src={badge}
                      alt="badge"
                      className="w-[80px] h-[80px] sm:w-[120px] sm:h-[120px] md:w-[150px] md:h-[150px]"
                    />
                  </div>
                )}

                <div className="flex flex-col flex-grow">
                  <h2 className="text-[18px] sm:text-[20px] md:text-[25px] font-medium leading-tight">
                    {plan.nameDescription}
                  </h2>

                  <div className="leading-none mb-2 mt-1">
                    <span className={`font-bold text-white ${plan.isFree || plan.price === 0 || plan.price === '0' ? "text-[20px] sm:text-[28px] md:text-[36px]" : "text-[28px] sm:text-[36px] md:text-[50px]"}`}>
                      {plan.isFree || plan.price === 0 || plan.price === '0' ? "30 Days Trial" : `$${plan.price}`}
                    </span>
                    {!(plan.isFree || plan.price === 0 || plan.price === '0') && (
                      <span className="text-[10px] sm:text-[12px] md:text-[14px] font-medium text-[#3978FF] ml-1">
                        /Month
                      </span>
                    )}
                  </div>

                  {/* Scrollable Description */}
                  <p
                    className="text-[10px] sm:text-xs md:text-sm font-normal text-gray-300 mb-4"
                    style={{ maxHeight: "100px", overflowY: "auto" }}
                  >
                    {plan.description}
                  </p>

                  {/* ✅ IMPROVED UI: Stats Box */}
                  {hasStats && (
                    <div className="bg-white/5 rounded-xl p-3 mb-4 space-y-2 border border-white/10">
                      {plan.truck > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-xs sm:text-sm">Trucks Limit</span>
                          <span className="text-white font-semibold text-sm">{plan.truck}</span>
                        </div>
                      )}
                      {plan.trackingCount > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-xs sm:text-sm">Tracking Limit</span>
                          <span className="text-white font-semibold text-sm">{plan.trackingCount}</span>
                        </div>
                      )}
                      {plan.loadsList > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-xs sm:text-sm">Loads Limit</span>
                          <span className="text-white font-semibold text-sm">{plan.loadsList}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Features List */}
                  <ul className="mt-2 sm:mt-3 space-y-2 flex-grow">
                    {plan.features?.length
                      ? plan.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-start text-[11px] sm:text-[13px] md:text-[15px] font-bold text-white"
                        >
                          <CheckCircle className="text-purple-400 mr-2 mt-1 w-3 h-3 sm:w-4 sm:h-4" />
                          {feature}
                        </li>
                      ))
                      : null}
                  </ul>

                  <div className="mt-3 sm:mt-4">
                    <button
                      onClick={() => {
                        const isCurrentActive = plan.name.toLowerCase() === userPackage || plan._id === userPackageId || plan._id === userAiPackageId;
                        if (isCurrentActive) {
                          navigate('/dashboard');
                        } else if (plan.isFree || plan.price === 0 || plan.price === '0') {
                          Swal.fire({
                            title: "Cannot Purchase",
                            text: "You cannot purchase the free plan. It is automatically assigned to new users.",
                            icon: "warning",
                            confirmButtonColor: "#04447A",
                            confirmButtonText: "OK",
                          });
                        } else {
                          handleChoosePlan(plan);
                        }
                      }}
                      disabled={dataLoading || paymentLoading}
                      className={`group w-full py-2 px-2 rounded-2xl transition ${
                          (plan.name.toLowerCase() === userPackage || plan._id === userPackageId || plan._id === userAiPackageId)
                            ? "bg-gray-700 cursor-pointer text-white text-sm sm:text-base hover:bg-gray-600"
                            : plan.isFree || plan.price === 0 || plan.price === '0'
                              ? "bg-[#0055F9]/10 text-blue-300 cursor-pointer text-sm sm:text-base hover:bg-[#0055F9]/20"
                              : dataLoading || paymentLoading
                                ? "bg-gray-500 cursor-not-allowed text-sm sm:text-base hover:opacity-90"
                                : "bg-[#0055F9] text-white text-sm sm:text-base hover:bg-[#0044C9] hover:opacity-90"
                        }`}
                    >
                      {(plan.name.toLowerCase() === userPackage || plan._id === userPackageId || plan._id === userAiPackageId) ? (
                        <>
                          <span className="block group-hover:hidden">Currently Active</span>
                          <span className="hidden group-hover:block">Go to Dashboard</span>
                        </>
                      ) : plan.isFree || plan.price === 0 || plan.price === '0' ? (
                        "Purchase Plan"
                      ) : (
                        "Upgrade Plan"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Empty State Message */}
          {filteredPackages.length === 0 && (
            <div className="col-span-full text-center py-10 text-gray-400">
              <p>No plans available in this category yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Package;